// A client's Overview. Ordered by care priority: risks first, then funding, what a worker needs to
// know, the remaining details and the clinical sections. Contacts, team, price book and the record
// summaries sit in the right-hand column (below the main column on narrow screens).
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  BookOpen, ChevronRight, ClipboardList, FileText, Lock, Mail, MapPin, Pencil, Phone,
  Plus, Stethoscope, Tag, Target, Trash2, User, UserCheck, Users,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import {
  Dialog, Field, FormRow, PersonAvatar, PriorityPill, SectionCard, Tabs, clientTypeLabel, fmtDate, plural,
} from '../../components/ui';
import { useClientRecord, ClientFrame } from './ClientLayout';
import ClientServiceTemplates from './ClientServiceTemplates';
import ClientFundingPlans from './ClientFundingPlans';
import ClientWorkerPreferences from './ClientWorkerPreferences';
import { ageOf, clientPayload, dayOf, daysUntil, formatAddress, fullName, genderLabel } from './clientShared';
import { ClientTypeSelect, GenderSelect, INVOICE_DELIVERY, STATES, invoiceDeliveryLabel } from './clientFields';

// ─── Small shared pieces ──────────────────────────────────────────────────────
// One edit style everywhere: a right-aligned ghost button — 'Edit' for a section, 'Add' for a list.
function CardAction({ icon: Icon = Pencil, children, className, ...rest }) {
  return (
    <button type="button" {...rest}
      className={clsx('inline-flex items-center gap-1.5 h-9 px-2.5 -my-1.5 rounded-lg text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50 transition-colors duration-150', className)}>
      <Icon size={14} aria-hidden />{children}
    </button>
  );
}

function IconAction({ icon: Icon, label, danger, ...rest }) {
  return (
    <button type="button" aria-label={label} title={label} {...rest}
      className={clsx('inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors duration-150',
        danger ? 'text-slate-400 hover:text-rose-600 hover:bg-rose-50' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100')}>
      <Icon size={14} aria-hidden />
    </button>
  );
}

function SubHead({ title, action }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-2 min-h-[2.25rem]">
      <h4 className="text-[13px] font-semibold text-slate-700">{title}</h4>
      {action}
    </div>
  );
}

function Fact({ label, children, className }) {
  return (
    <div className={className}>
      <dt className="text-[12.5px] text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap break-words">{children || <span className="text-slate-500">—</span>}</dd>
    </div>
  );
}

function SaveCancel({ onCancel, onSave, saving, saveLabel = 'Save', disabled }) {
  return (
    <div className="flex justify-end gap-2 pt-1">
      <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
      <button type="button" onClick={onSave} disabled={saving || disabled} className="btn-primary">{saving ? 'Saving…' : saveLabel}</button>
    </div>
  );
}

function CardSkeleton({ lines = 3 }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" aria-hidden>
      <div className="skeleton h-4 w-40" />
      {Array.from({ length: lines }).map((_, i) => <div key={i} className="skeleton h-4" />)}
    </div>
  );
}

// ─── Key-value section (clinical sub-sections, care review) ───────────────────
function SimpleSection({ fields, data, onSave, emptyText }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(data || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(data || {}); }, [data]);
  const hasData = fields.some((f) => data?.[f.key]);

  async function save() {
    setSaving(true);
    try { if (await onSave(form)) setEditing(false); } finally { setSaving(false); }
  }

  if (!editing) {
    return (
      <div>
        <div className="flex justify-end mb-2"><CardAction onClick={() => setEditing(true)}>{hasData ? 'Edit' : 'Add details'}</CardAction></div>
        {!hasData ? <p className="text-sm text-slate-500">{emptyText}</p> : (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.filter((f) => data?.[f.key]).map((f) => (
              <Fact key={f.key} label={f.label} className={f.type === 'textarea' ? 'sm:col-span-2' : undefined}>
                {f.type === 'date' ? fmtDate(data[f.key]) : data[f.key]}
              </Fact>
            ))}
          </dl>
        )}
      </div>
    );
  }

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  return (
    <div className="space-y-4">
      <FormRow cols={2}>
        {fields.map((f) => (
          <Field key={f.key} label={f.label} className={f.type === 'textarea' ? 'sm:col-span-2' : undefined}>
            {(fid) => (f.type === 'textarea'
              ? <textarea id={fid} className="input-field" rows={3} value={form[f.key] || ''} onChange={(e) => set(f.key, e.target.value)} />
              : <input id={fid} type={f.type === 'date' ? 'date' : 'text'} className="input-field" value={form[f.key] || ''} onChange={(e) => set(f.key, e.target.value)} />)}
          </Field>
        ))}
      </FormRow>
      <SaveCancel onCancel={() => { setEditing(false); setForm(data || {}); }} onSave={save} saving={saving} />
    </div>
  );
}

// ─── BMI ──────────────────────────────────────────────────────────────────────
function BMISection({ data, onSave }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(data || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(data || {}); }, [data]);

  const calcBMI = () => {
    const h = parseFloat(form.height_cm); const w = parseFloat(form.weight_kg);
    if (!h || !w) return null;
    return (w / ((h / 100) ** 2)).toFixed(1);
  };
  const bmi = calcBMI();
  const category = (v) => (!v ? null : v < 18.5 ? 'Underweight' : v < 25 ? 'Healthy weight' : v < 30 ? 'Overweight' : 'Obese');

  async function save() {
    setSaving(true);
    try { if (await onSave({ ...form, bmi: bmi || '' })) setEditing(false); } finally { setSaving(false); }
  }

  const hasData = data?.height_cm || data?.weight_kg;
  if (!editing) {
    return (
      <div>
        <div className="flex justify-end mb-2"><CardAction onClick={() => setEditing(true)}>{hasData ? 'Edit' : 'Add BMI'}</CardAction></div>
        {!hasData ? <p className="text-sm text-slate-500">No height or weight recorded. Add them to track BMI over time.</p> : (
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {data.height_cm && <Fact label="Height">{data.height_cm} cm</Fact>}
            {data.weight_kg && <Fact label="Weight">{data.weight_kg} kg</Fact>}
            {data.bmi && <Fact label="BMI">{data.bmi} ({category(Number(data.bmi))})</Fact>}
            {data.date_measured && <Fact label="Measured">{fmtDate(data.date_measured)}</Fact>}
            {data.notes && <Fact label="Notes" className="col-span-2 sm:col-span-4">{data.notes}</Fact>}
          </dl>
        )}
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4">
      <FormRow cols={3}>
        <Field label="Height (cm)">{(fid) => <input id={fid} type="number" inputMode="decimal" className="input-field" value={form.height_cm || ''} onChange={(e) => set('height_cm', e.target.value)} />}</Field>
        <Field label="Weight (kg)">{(fid) => <input id={fid} type="number" inputMode="decimal" className="input-field" value={form.weight_kg || ''} onChange={(e) => set('weight_kg', e.target.value)} />}</Field>
        <Field label="Date measured">{(fid) => <input id={fid} type="date" className="input-field" value={form.date_measured || ''} onChange={(e) => set('date_measured', e.target.value)} />}</Field>
      </FormRow>
      {bmi && <p className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700">BMI <strong className="tabular-nums">{bmi}</strong> ({category(Number(bmi))})</p>}
      <Field label="Notes">{(fid) => <textarea id={fid} className="input-field" rows={2} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />}</Field>
      <SaveCancel onCancel={() => { setEditing(false); setForm(data || {}); }} onSave={save} saving={saving} />
    </div>
  );
}

// ─── Risks and issues ─────────────────────────────────────────────────────────
const RISK_LEVELS = ['low', 'medium', 'high', 'critical'];
const EMPTY_RISK = { risk_description: '', risk_level: 'medium', mitigation: '', status: 'active' };

function RisksSection({ data, onSave }) {
  const confirm = useConfirm();
  const [risks, setRisks] = useState(data || []);
  const [dialog, setDialog] = useState(null); // { index: number|null }
  const [form, setForm] = useState(EMPTY_RISK);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRisks(data || []); }, [data]);

  const openAdd = () => { setForm(EMPTY_RISK); setDialog({ index: null }); };
  const openEdit = (i) => { setForm({ ...EMPTY_RISK, ...risks[i] }); setDialog({ index: i }); };

  async function save(e) {
    if (e) e.preventDefault();
    if (!form.risk_description.trim()) return;
    setSaving(true);
    const next = dialog.index !== null
      ? risks.map((r, i) => (i === dialog.index ? { ...r, ...form } : r))
      : [...risks, { ...form, id: Date.now().toString() }];
    try { if (await onSave(next)) { setRisks(next); setDialog(null); } } finally { setSaving(false); }
  }

  async function remove(i) {
    const r = risks[i];
    if (!await confirm(`“${r.risk_description}” is removed from this client's risks.`, { title: 'Remove this risk?', danger: true, confirmLabel: 'Remove risk' })) return;
    const next = risks.filter((_, ri) => ri !== i);
    if (await onSave(next)) setRisks(next);
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-[13px] text-slate-600">What a worker must manage to keep this participant, and themselves, safe.</p>
        <CardAction icon={Plus} onClick={openAdd}>Add risk</CardAction>
      </div>
      {risks.length === 0 ? (
        <p className="text-sm text-slate-500">No risks or issues recorded yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {risks.map((r, i) => (
            <li key={r.id || i} className="flex items-start gap-3 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-800">{r.risk_description}</span>
                  <PriorityPill priority={r.risk_level || 'medium'} />
                  {r.status === 'monitoring' && <span className="badge-yellow">Monitoring</span>}
                  {r.status === 'resolved' && <span className="badge-gray">Resolved</span>}
                </div>
                {r.mitigation && <p className="text-[13px] text-slate-600 mt-1 whitespace-pre-wrap">{r.mitigation}</p>}
              </div>
              <div className="flex shrink-0 -mr-2">
                <IconAction icon={Pencil} label={`Edit risk: ${r.risk_description}`} onClick={() => openEdit(i)} />
                <IconAction icon={Trash2} label={`Remove risk: ${r.risk_description}`} danger onClick={() => remove(i)} />
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!dialog} onClose={() => setDialog(null)} title={dialog && dialog.index !== null ? 'Edit risk' : 'Add a risk or issue'}
        footer={<>
          <button type="button" onClick={() => setDialog(null)} className="btn-secondary">Cancel</button>
          <button type="submit" form="risk-form" disabled={saving || !form.risk_description.trim()} className="btn-primary">{saving ? 'Saving…' : 'Save risk'}</button>
        </>}>
        <form id="risk-form" onSubmit={save} className="space-y-4">
          <Field label="Risk or issue" required>
            {(fid) => <textarea id={fid} className="input-field" rows={2} required value={form.risk_description} onChange={(e) => set('risk_description', e.target.value)} />}
          </Field>
          <FormRow cols={2}>
            <Field label="Level">
              {(fid) => (
                <select id={fid} className="input-field" value={form.risk_level} onChange={(e) => set('risk_level', e.target.value)}>
                  {RISK_LEVELS.map((l) => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
                </select>
              )}
            </Field>
            <Field label="Status">
              {(fid) => (
                <select id={fid} className="input-field" value={form.status} onChange={(e) => set('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="monitoring">Monitoring</option>
                  <option value="resolved">Resolved</option>
                </select>
              )}
            </Field>
          </FormRow>
          <Field label="How it's managed" hint="The strategy workers follow on shift.">
            {(fid) => <textarea id={fid} className="input-field" rows={3} value={form.mitigation} onChange={(e) => set('mitigation', e.target.value)} />}
          </Field>
        </form>
      </Dialog>
    </div>
  );
}

// ─── Heading + content list (need to know, useful information) ────────────────
function NoteList({ title, items, onSave, emptyText, noun }) {
  const confirm = useConfirm();
  const [list, setList] = useState(items || []);
  const [dialog, setDialog] = useState(null); // { index: number|null }
  const [form, setForm] = useState({ heading: '', content: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setList(items || []); }, [items]);

  const formId = `note-form-${noun.replace(/\W+/g, '-')}`;
  const openAdd = () => { setForm({ heading: '', content: '' }); setDialog({ index: null }); };
  const openEdit = (i) => { setForm({ heading: list[i].heading || '', content: list[i].content || '' }); setDialog({ index: i }); };

  async function save(e) {
    if (e) e.preventDefault();
    if (!form.heading && !form.content) return;
    setSaving(true);
    const next = dialog.index !== null
      ? list.map((x, i) => (i === dialog.index ? { ...x, ...form } : x))
      : [...list, { id: Date.now().toString(), ...form, added_at: new Date().toISOString().split('T')[0] }];
    try { if (await onSave(next)) { setList(next); setDialog(null); } } finally { setSaving(false); }
  }

  async function remove(i) {
    const item = list[i];
    if (!await confirm(`“${item.heading || item.content}” is removed from ${title.toLowerCase()}.`, { title: `Remove this ${noun}?`, danger: true, confirmLabel: 'Remove' })) return;
    const next = list.filter((_, ri) => ri !== i);
    if (await onSave(next)) setList(next);
  }

  return (
    <div>
      <SubHead title={title} action={<CardAction icon={Plus} onClick={openAdd}>Add</CardAction>} />
      {list.length === 0 ? <p className="text-sm text-slate-500">{emptyText}</p> : (
        <ul className="space-y-2">
          {list.map((item, i) => (
            <li key={item.id || i} className="rounded-lg border border-slate-200 px-3 py-2 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                {item.heading && <p className="text-[13px] font-semibold text-slate-800">{item.heading}</p>}
                {item.content && <p className="text-[13px] text-slate-600 mt-0.5 whitespace-pre-wrap">{item.content}</p>}
              </div>
              <div className="flex shrink-0 -my-1 -mr-1.5">
                <IconAction icon={Pencil} label={`Edit ${item.heading || noun}`} onClick={() => openEdit(i)} />
                <IconAction icon={Trash2} label={`Remove ${item.heading || noun}`} danger onClick={() => remove(i)} />
              </div>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} title={dialog && dialog.index !== null ? `Edit ${noun}` : `Add ${noun}`}
        footer={<>
          <button type="button" onClick={() => setDialog(null)} className="btn-secondary">Cancel</button>
          <button type="submit" form={formId} disabled={saving || (!form.heading && !form.content)} className="btn-primary">{saving ? 'Saving…' : 'Save'}</button>
        </>}>
        <form id={formId} onSubmit={save} className="space-y-4">
          <Field label="Heading">{(fid) => <input id={fid} className="input-field" value={form.heading} onChange={(e) => setForm((f) => ({ ...f, heading: e.target.value }))} placeholder="For example: Morning routine" />}</Field>
          <Field label="Details">{(fid) => <textarea id={fid} className="input-field" rows={4} value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} />}</Field>
        </form>
      </Dialog>
    </div>
  );
}

// ─── General information (visible to carers) ──────────────────────────────────
function GeneralInfoEditor({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!editing) setText(value || ''); }, [value, editing]);

  async function save() {
    setSaving(true);
    try { if (await onSave(text)) setEditing(false); } finally { setSaving(false); }
  }

  return (
    <div>
      <SubHead title="General information" action={!editing && <CardAction onClick={() => setEditing(true)}>{value ? 'Edit' : 'Add'}</CardAction>} />
      {editing ? (
        <div className="space-y-2">
          <textarea className="input-field" rows={4} value={text} onChange={(e) => setText(e.target.value)} aria-label="General information" />
          <SaveCancel onCancel={() => { setEditing(false); setText(value || ''); }} onSave={save} saving={saving} />
        </div>
      ) : value ? (
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{value}</p>
      ) : (
        <p className="text-sm text-slate-500">Nothing yet. Add preferences, routines and how this participant likes to communicate. Workers see it on every shift.</p>
      )}
    </div>
  );
}

// ─── Support needs ────────────────────────────────────────────────────────────
function SupportNeeds({ clientId, current, all, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/support-needs`, { support_need_ids: selected });
      toast.success('Support needs updated');
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save support needs'); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <SubHead title="Support needs" action={!editing && (
        <CardAction onClick={() => { setSelected(current.map((n) => n.id)); setEditing(true); }}>Edit</CardAction>
      )} />
      {editing ? (
        <div className="space-y-3">
          {all.length === 0 ? <p className="text-sm text-slate-500">No support needs are set up yet. Add them in Settings first.</p> : (
            <div className="flex flex-wrap gap-2" role="group" aria-label="Support needs">
              {all.map((sn) => {
                const on = selected.includes(sn.id);
                return (
                  <button key={sn.id} type="button" aria-pressed={on}
                    onClick={() => setSelected((ids) => (on ? ids.filter((x) => x !== sn.id) : [...ids, sn.id]))}
                    className={clsx('h-8 px-3 rounded-full text-[13px] font-medium border transition-colors duration-150',
                      on ? 'bg-primary-50 text-primary-700 border-primary-300' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300')}>
                    {sn.name}
                  </button>
                );
              })}
            </div>
          )}
          <SaveCancel onCancel={() => setEditing(false)} onSave={save} saving={saving} />
        </div>
      ) : current.length === 0 ? (
        <p className="text-sm text-slate-500">None recorded. Add support needs so shifts are matched to workers with the right skills.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {current.map((sn) => <span key={sn.id} className="badge-gray">{sn.name}</span>)}
        </div>
      )}
    </div>
  );
}

// ─── Details (the facts the header strip doesn't show) + quick edit ───────────
function DetailsCard({ client, onSave }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const editing = !!form;

  const start = () => setForm({
    firstName: client.first_name || '', lastName: client.last_name || '',
    dateOfBirth: dayOf(client.date_of_birth), gender: client.gender || '',
    ndisNumber: client.ndis_number || '', clientType: client.client_type || 'ndis_managed',
    referenceNumber: client.reference_number || '', nextReviewDate: dayOf(client.next_review_date),
    planPeriodStart: dayOf(client.plan_period_start), planPeriodEnd: dayOf(client.plan_period_end),
    invoiceDeliveryMethod: client.invoice_delivery_method || 'email',
    address: client.address || '', suburb: client.suburb || '', state: client.state || '', postcode: client.postcode || '',
    latitude: client.latitude ?? null, longitude: client.longitude ?? null,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!form.firstName.trim() || !form.lastName.trim()) { toast.error('Enter a first and last name'); return; }
    setSaving(true);
    try { if (await onSave(form)) setForm(null); } finally { setSaving(false); }
  }

  const planPeriod = client.plan_period_start || client.plan_period_end
    ? `${client.plan_period_start ? fmtDate(dayOf(client.plan_period_start)) : '…'} – ${client.plan_period_end ? fmtDate(dayOf(client.plan_period_end)) : '…'}`
    : null;
  const address = formatAddress(client);

  return (
    <SectionCard title="Personal information" icon={User} bodyClass="px-5 py-4"
      actions={!editing && <CardAction onClick={start}>Edit</CardAction>}>
      {!editing ? (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          <Fact label="Full name">{fullName(client)}</Fact>
          <Fact label="Date of birth">
            {client.date_of_birth ? `${fmtDate(dayOf(client.date_of_birth))}${ageOf(client.date_of_birth) != null ? ` (${ageOf(client.date_of_birth)})` : ''}` : null}
          </Fact>
          <Fact label="Gender">{genderLabel(client.gender)}</Fact>
          <Fact label="NDIS number"><span className="tabular-nums">{client.ndis_number}</span></Fact>
          <Fact label="Plan management">{client.client_type ? clientTypeLabel(client.client_type) : null}</Fact>
          <Fact label="Next review">{client.next_review_date ? fmtDate(dayOf(client.next_review_date)) : null}</Fact>
          <Fact label="Reference number">{client.reference_number}</Fact>
          <Fact label="Plan period">{planPeriod}</Fact>
          <Fact label="Invoice delivery">{client.invoice_delivery_method ? invoiceDeliveryLabel(client.invoice_delivery_method) : null}</Fact>
          {client.mobile && <Fact label="Mobile"><a href={`tel:${String(client.mobile).replace(/\s+/g, '')}`} className="text-primary-700 hover:underline">{client.mobile}</a></Fact>}
          <Fact label="Address" className="sm:col-span-2">
            {address && <span className="inline-flex items-start gap-1.5"><MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" aria-hidden />{address}</span>}
          </Fact>
        </dl>
      ) : (
        <div className="space-y-4">
          <FormRow cols={2}>
            <Field label="First name" required>{(fid) => <input id={fid} className="input-field" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />}</Field>
            <Field label="Last name" required>{(fid) => <input id={fid} className="input-field" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />}</Field>
            <Field label="Date of birth">{(fid) => <input id={fid} type="date" className="input-field" value={form.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />}</Field>
            <Field label="Gender">{(fid) => <GenderSelect id={fid} value={form.gender} onChange={(v) => set('gender', v)} />}</Field>
            <Field label="NDIS number">{(fid) => <input id={fid} inputMode="numeric" className="input-field tabular-nums" value={form.ndisNumber} onChange={(e) => set('ndisNumber', e.target.value)} />}</Field>
            <Field label="Plan management">{(fid) => <ClientTypeSelect id={fid} value={form.clientType} onChange={(v) => set('clientType', v)} />}</Field>
            <Field label="Reference number">{(fid) => <input id={fid} className="input-field" value={form.referenceNumber} onChange={(e) => set('referenceNumber', e.target.value)} />}</Field>
            <Field label="Next review">{(fid) => <input id={fid} type="date" className="input-field" value={form.nextReviewDate} onChange={(e) => set('nextReviewDate', e.target.value)} />}</Field>
            <Field label="Plan period start">{(fid) => <input id={fid} type="date" className="input-field" value={form.planPeriodStart} onChange={(e) => set('planPeriodStart', e.target.value)} />}</Field>
            <Field label="Plan period end">{(fid) => <input id={fid} type="date" className="input-field" value={form.planPeriodEnd} onChange={(e) => set('planPeriodEnd', e.target.value)} />}</Field>
            <Field label="Invoice delivery">
              {(fid) => (
                <select id={fid} className="input-field" value={form.invoiceDeliveryMethod} onChange={(e) => set('invoiceDeliveryMethod', e.target.value)}>
                  {INVOICE_DELIVERY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
            </Field>
          </FormRow>
          <div className="pt-4 border-t border-slate-100 space-y-4">
            <Field label="Street address" hint={form.latitude != null ? 'Location pinned: the clock-in geofence uses this exact spot.' : undefined}>
              {(fid) => (
                <AddressAutocomplete
                  id={fid}
                  value={form.address}
                  onChange={(v) => set('address', v)}
                  onSelect={(a) => setForm((f) => ({ ...f, address: a.street || f.address, suburb: a.suburb || f.suburb, state: a.state || f.state, postcode: a.postcode || f.postcode, latitude: a.lat, longitude: a.lon }))}
                  className="input-field"
                  placeholder="Start typing an address…"
                />
              )}
            </Field>
            <FormRow cols={3}>
              <Field label="Suburb">{(fid) => <input id={fid} className="input-field" value={form.suburb} onChange={(e) => set('suburb', e.target.value)} />}</Field>
              <Field label="State">
                {(fid) => (
                  <select id={fid} className="input-field" value={form.state} onChange={(e) => set('state', e.target.value)}>
                    <option value="">Select…</option>
                    {STATES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                )}
              </Field>
              <Field label="Postcode">{(fid) => <input id={fid} inputMode="numeric" maxLength={4} className="input-field tabular-nums" value={form.postcode} onChange={(e) => set('postcode', e.target.value)} />}</Field>
            </FormRow>
          </div>
          <SaveCancel onCancel={() => setForm(null)} onSave={save} saving={saving} saveLabel="Save changes" />
        </div>
      )}
    </SectionCard>
  );
}

// ─── Clinical details: one card, sub-tabs instead of a stack of accordions ─────
const CLINICAL_TABS = [
  { id: 'risks', label: 'Risks and issues' },
  { id: 'environment', label: 'Environment' },
  { id: 'psychological', label: 'Psychological' },
  { id: 'sensory', label: 'Sensory' },
  { id: 'bmi', label: 'BMI and health' },
  { id: 'review', label: 'Care review' },
];

function ClinicalCard({ client, saveSection }) {
  const [tab, setTab] = useState('risks');
  const risks = client.risks_and_issues || [];
  const tabs = CLINICAL_TABS.map((t) => (t.id === 'risks' && risks.length ? { ...t, count: risks.length } : t));
  return (
    <SectionCard title="Clinical details" icon={Stethoscope} bodyClass="px-5 pb-5">
      <Tabs tabs={tabs} value={tab} onChange={setTab} ariaLabel="Clinical details" className="-mx-5 px-3 mb-4" />
      <div role="tabpanel" aria-label={(CLINICAL_TABS.find((t) => t.id === tab) || {}).label}>
        {tab === 'risks' && <RisksSection data={risks} onSave={(d) => saveSection('risks_and_issues', d)} />}
        {tab === 'environment' && (
          <SimpleSection data={client.environmental_details || {}} onSave={(d) => saveSection('environmental_details', d)}
            emptyText="No home details yet. Add access notes, pets and mobility aids so workers arrive prepared."
            fields={[
              { key: 'home_type', label: 'Home type' },
              { key: 'living_arrangement', label: 'Living arrangement' },
              { key: 'access_notes', label: 'Access and entry', type: 'textarea' },
              { key: 'pets', label: 'Pets' },
              { key: 'mobility_aids', label: 'Mobility aids' },
              { key: 'home_modifications', label: 'Home modifications', type: 'textarea' },
              { key: 'other', label: 'Other details', type: 'textarea' },
            ]} />
        )}
        {tab === 'psychological' && (
          <SimpleSection data={client.psychological_details || {}} onSave={(d) => saveSection('psychological_details', d)}
            emptyText="Nothing recorded. Add diagnoses, known triggers and what helps."
            fields={[
              { key: 'diagnoses', label: 'Diagnoses or conditions', type: 'textarea' },
              { key: 'behavioral_notes', label: 'Behavioural notes', type: 'textarea' },
              { key: 'triggers', label: 'Known triggers', type: 'textarea' },
              { key: 'coping_strategies', label: 'Coping strategies', type: 'textarea' },
              { key: 'mental_health_contact', label: 'Mental health contact' },
              { key: 'other', label: 'Other notes', type: 'textarea' },
            ]} />
        )}
        {tab === 'sensory' && (
          <SimpleSection data={client.sensory_details || {}} onSave={(d) => saveSection('sensory_details', d)}
            emptyText="Nothing recorded. Add vision, hearing and communication needs."
            fields={[
              { key: 'vision', label: 'Vision', type: 'textarea' },
              { key: 'hearing', label: 'Hearing', type: 'textarea' },
              { key: 'sensory_sensitivities', label: 'Sensory sensitivities', type: 'textarea' },
              { key: 'communication_needs', label: 'Communication needs', type: 'textarea' },
              { key: 'other', label: 'Other details', type: 'textarea' },
            ]} />
        )}
        {tab === 'bmi' && <BMISection data={client.bmi_data || {}} onSave={(d) => saveSection('bmi_data', d)} />}
        {tab === 'review' && (
          <SimpleSection data={client.care_review_data || {}} onSave={(d) => saveSection('care_review_data', d)}
            emptyText="No care review recorded. Add the last review and who did it."
            fields={[
              { key: 'last_review_date', label: 'Last review', type: 'date' },
              { key: 'next_review_date', label: 'Next review', type: 'date' },
              { key: 'reviewed_by', label: 'Reviewed by' },
              { key: 'review_type', label: 'Review type' },
              { key: 'review_notes', label: 'Review notes', type: 'textarea' },
            ]} />
        )}
      </div>
    </SectionCard>
  );
}

// ─── Contacts ─────────────────────────────────────────────────────────────────
const RELATIONS = ['Guardian', 'Parent', 'Spouse', 'Child', 'Sibling', 'Friend', 'Carer', 'Support Coordinator', 'Plan Manager', 'GP', 'Other'];

function ContactDialog({ clientId, contact, open, onSaved, onClose }) {
  const isEdit = !!contact;
  const blank = { firstName: '', lastName: '', phone: '', email: '', relationCategory: '', isPrimary: false, isBillingContact: false };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(contact ? {
      firstName: contact.first_name || '', lastName: contact.last_name || '',
      phone: contact.phone || '', email: contact.email || '',
      relationCategory: contact.relation_category || '',
      isPrimary: !!contact.is_primary, isBillingContact: !!contact.is_billing_contact,
    } : blank);
  }, [open, contact]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save(e) {
    if (e) e.preventDefault();
    if (!form.firstName.trim()) return;
    setSaving(true);
    try {
      const r = isEdit
        ? await api.put(`/clients/${clientId}/contacts/${contact.id}`, form)
        : await api.post(`/clients/${clientId}/contacts`, form);
      toast.success(isEdit ? 'Contact updated' : 'Contact added');
      onSaved(r.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Could not save the contact'); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? 'Edit contact' : 'Add contact'}
      footer={<>
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" form="contact-form" disabled={saving || !form.firstName.trim()} className="btn-primary">{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add contact'}</button>
      </>}>
      <form id="contact-form" onSubmit={save} className="space-y-4">
        <FormRow cols={2}>
          <Field label="First name" required>{(fid) => <input id={fid} className="input-field" required value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />}</Field>
          <Field label="Last name">{(fid) => <input id={fid} className="input-field" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />}</Field>
          <Field label="Phone">{(fid) => <input id={fid} type="tel" inputMode="tel" className="input-field" value={form.phone} onChange={(e) => set('phone', e.target.value)} />}</Field>
          <Field label="Email">{(fid) => <input id={fid} type="email" className="input-field" value={form.email} onChange={(e) => set('email', e.target.value)} />}</Field>
        </FormRow>
        <Field label="Relationship">
          {(fid) => (
            <select id={fid} className="input-field" value={form.relationCategory} onChange={(e) => set('relationCategory', e.target.value)}>
              <option value="">Select…</option>
              {RELATIONS.map((r) => <option key={r}>{r}</option>)}
            </select>
          )}
        </Field>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={form.isPrimary} onChange={(e) => set('isPrimary', e.target.checked)} className="rounded border-slate-300" />
            Primary contact
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={form.isBillingContact} onChange={(e) => set('isBillingContact', e.target.checked)} className="rounded border-slate-300" />
            Billing contact
          </label>
        </div>
      </form>
    </Dialog>
  );
}

function ContactsCard({ client, onChanged }) {
  const confirm = useConfirm();
  const [dialog, setDialog] = useState({ open: false, contact: null });
  const contacts = client.contacts || [];

  async function remove(c) {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'this contact';
    if (!await confirm(`${name} is removed from ${fullName(client)}'s contacts.`, { title: 'Delete contact?', danger: true, confirmLabel: 'Delete contact' })) return;
    try { await api.delete(`/clients/${client.id}/contacts/${c.id}`); toast.success('Contact removed'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not delete the contact'); }
  }

  return (
    <SectionCard title="Contacts" icon={Users} bodyClass="px-4 py-2"
      actions={<CardAction icon={Plus} onClick={() => setDialog({ open: true, contact: null })}>Add</CardAction>}>
      {contacts.length === 0 ? (
        <p className="text-sm text-slate-500 py-2">No contacts yet. Add family, a guardian or the plan manager so the office can reach someone quickly.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {contacts.map((c) => (
            <li key={c.id} className="flex items-start gap-3 py-3">
              <PersonAvatar id={c.id} firstName={c.first_name} lastName={c.last_name} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{`${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unnamed'}</p>
                {c.relation_category && <p className="text-[12.5px] text-slate-500">{c.relation_category}</p>}
                {c.phone && (
                  <a href={`tel:${String(c.phone).replace(/\s+/g, '')}`} className="mt-0.5 flex items-center gap-1 text-[13px] text-primary-700 hover:underline">
                    <Phone size={12} aria-hidden /> {c.phone}
                  </a>
                )}
                {c.email && (
                  <a href={`mailto:${c.email}`} className="mt-0.5 flex items-center gap-1 text-[13px] text-primary-700 hover:underline truncate">
                    <Mail size={12} aria-hidden /> <span className="truncate">{c.email}</span>
                  </a>
                )}
                {(c.is_primary || c.is_billing_contact) && (
                  <div className="flex gap-1.5 mt-1.5">
                    {c.is_primary && <span className="badge-gray !text-[11px]">Primary</span>}
                    {c.is_billing_contact && <span className="badge-gray !text-[11px]">Billing</span>}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 -mr-2">
                <IconAction icon={Pencil} label={`Edit ${c.first_name || 'contact'}`} onClick={() => setDialog({ open: true, contact: c })} />
                <IconAction icon={Trash2} label={`Delete ${c.first_name || 'contact'}`} danger onClick={() => remove(c)} />
              </div>
            </li>
          ))}
        </ul>
      )}
      <ContactDialog clientId={client.id} contact={dialog.contact} open={dialog.open}
        onSaved={() => onChanged()} onClose={() => setDialog({ open: false, contact: null })} />
    </SectionCard>
  );
}

// ─── Price book: shown once, changed deliberately ─────────────────────────────
function PriceBookCard({ client, priceBooks, onChanged }) {
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const currentName = client.price_book_name || (priceBooks.find((p) => p.id === client.default_price_book_id) || {}).name || null;

  async function change(nextId) {
    if ((nextId || '') === (client.default_price_book_id || '')) { setEditing(false); return; }
    const nextName = nextId ? ((priceBooks.find((p) => p.id === nextId) || {}).name || 'the selected price book') : null;
    const ok = await confirm(
      `Changing the price book reprices future shifts. ${fullName(client)} moves from ${currentName || 'no price book'} to ${nextName || 'no price book'}.`,
      { title: nextId ? `Use ${nextName}?` : 'Remove the price book?', confirmLabel: nextId ? 'Change price book' : 'Remove price book' },
    );
    if (!ok) return;
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}/price-book`, { priceBookId: nextId || null });
      toast.success(nextId ? 'Price book changed' : 'Price book removed');
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not change the price book'); }
    finally { setSaving(false); }
  }

  return (
    <SectionCard title="Price book" icon={Tag} bodyClass="px-4 py-3"
      actions={!editing && <CardAction onClick={() => setEditing(true)}>Change</CardAction>}>
      {editing ? (
        <div className="space-y-2">
          <select className="input-field" defaultValue={client.default_price_book_id || ''} disabled={saving}
            onChange={(e) => change(e.target.value)} aria-label="Price book">
            <option value="">No price book</option>
            {priceBooks.map((pb) => <option key={pb.id} value={pb.id}>{pb.name}</option>)}
          </select>
          <p className="text-[12px] text-slate-500">Changing the price book reprices future shifts.</p>
          <button type="button" onClick={() => setEditing(false)} className="btn-secondary w-full" disabled={saving}>Cancel</button>
        </div>
      ) : (
        <p className={clsx('text-sm', currentName ? 'text-slate-800 font-medium' : 'text-slate-500')}>{currentName || 'No price book assigned'}</p>
      )}
    </SectionCard>
  );
}

// ─── Teams ────────────────────────────────────────────────────────────────────
function TeamsCard({ teams }) {
  return (
    <SectionCard title="Teams" icon={Users} bodyClass="px-4 py-3"
      actions={<Link to="/teams" className="inline-flex items-center h-9 px-2.5 -my-1.5 rounded-lg text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100">Manage</Link>}>
      {!teams?.length ? (
        <p className="text-sm text-slate-500">Not in a team. <Link to="/teams" className="text-primary-700 hover:underline">Assign a team</Link> to roster this participant with a regular group of workers.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {teams.map((t) => <Link key={t.id} to="/teams" className="badge-gray hover:bg-slate-200">{t.name}</Link>)}
        </div>
      )}
    </SectionCard>
  );
}

// ─── One-line summaries of the other tabs (replaces the duplicate cards) ───────
function RecordsCard({ clientId, documents, goals, plans }) {
  const docs = documents || [];
  const expired = docs.filter((d) => { const n = daysUntil(d.expiry_date); return n != null && n < 0; }).length;
  const expiring = docs.filter((d) => { const n = daysUntil(d.expiry_date); return n != null && n >= 0 && n <= 30; }).length;
  const achieved = (goals || []).filter((g) => g.achieved_at).length;
  const planGoals = (plans || []).reduce((s, p) => s + (Number(p.goal_count) || 0), 0);
  const rows = [
    {
      to: `/clients/${clientId}/documents`, icon: FileText, label: 'Documents',
      text: docs.length ? [plural(docs.length, 'document'), expired && `${expired} expired`, expiring && `${expiring} expiring`].filter(Boolean).join(' · ') : 'No documents yet',
      alert: expired > 0 ? 'rose' : expiring > 0 ? 'amber' : null,
    },
    {
      to: `/clients/${clientId}/goals`, icon: Target, label: 'Goals',
      text: goals == null ? 'Loading…' : goals.length ? `${plural(goals.length, 'goal')} · ${achieved} achieved` : 'No goals yet',
    },
    {
      to: `/clients/${clientId}/care-plan`, icon: ClipboardList, label: 'Care plans',
      text: plans == null ? 'Loading…' : plans.length ? `${plural(plans.length, 'care plan')} · ${plural(planGoals, 'goal')}` : 'No care plan yet',
    },
  ];
  return (
    <SectionCard title="Records" icon={BookOpen} bodyClass="py-1">
      <ul>
        {rows.map((r) => (
          <li key={r.label}>
            <Link to={r.to} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors duration-150">
              <r.icon size={15} className="text-slate-400 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-slate-800">{r.label}</span>
                <span className={clsx('block text-[12.5px] truncate', r.alert === 'rose' ? 'text-rose-700' : r.alert === 'amber' ? 'text-amber-700' : 'text-slate-500')}>{r.text}</span>
              </span>
              <ChevronRight size={15} className="text-slate-400 shrink-0" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

// ─── Referral stakeholders ────────────────────────────────────────────────────
function ClientStakeholders({ clientId }) {
  const confirm = useConfirm();
  const [assignments, setAssignments] = useState([]);
  const [allStakeholders, setAllStakeholders] = useState([]);
  const [modal, setModal] = useState(null); // null | { role, assignment? }
  const [form, setForm] = useState({ stakeholder_id: '', profit_percentage: '', notes: '' });
  const [saving, setSaving] = useState(false);

  function load() {
    if (!clientId) return; // don't fetch with an undefined id → backend 22P02 (500)
    api.get(`/financials/client-stakeholders/${clientId}`).then((r) => setAssignments(r.data || [])).catch(() => {});
  }

  useEffect(() => {
    api.get('/financials/stakeholders').then((r) => setAllStakeholders(r.data || [])).catch(() => {});
    if (clientId) load();
  }, [clientId]);

  function openAdd(role) { setForm({ stakeholder_id: '', profit_percentage: '', notes: '' }); setModal({ role }); }
  function openEdit(a) { setForm({ stakeholder_id: a.stakeholder_id, profit_percentage: a.profit_percentage, notes: a.notes || '' }); setModal({ role: a.role, assignment: a }); }

  async function save(e) {
    if (e) e.preventDefault();
    if (!form.stakeholder_id) return toast.error('Choose a stakeholder');
    if (!form.profit_percentage || Number.isNaN(parseFloat(form.profit_percentage))) return toast.error('Enter the percentage of client profit');
    if (!clientId) return toast.error('Client ID missing');
    setSaving(true);
    try {
      const body = { stakeholder_id: form.stakeholder_id, role: modal.role, profit_percentage: parseFloat(form.profit_percentage), notes: form.notes };
      if (modal.assignment) await api.put(`/financials/client-stakeholders/${clientId}/${modal.assignment.id}`, body);
      else await api.post(`/financials/client-stakeholders/${clientId}`, body);
      toast.success('Stakeholder saved');
      setModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || err.message || 'Could not save'); }
    finally { setSaving(false); }
  }

  async function del(a) {
    if (!await confirm(`${a.stakeholder_name} stops receiving a share of this client's profit.`, { title: `Remove ${a.role} stakeholder?`, danger: true, confirmLabel: 'Remove' })) return;
    try { await api.delete(`/financials/client-stakeholders/${clientId}/${a.id}`); toast.success('Stakeholder removed'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not remove the stakeholder'); }
  }

  const primary = assignments.find((a) => a.role === 'primary');
  const secondary = assignments.find((a) => a.role === 'secondary');

  const RoleRow = ({ label, assignment, role }) => (
    <div className={clsx('rounded-lg border px-3 py-2.5', assignment ? 'border-slate-200' : 'border-dashed border-slate-300')}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-medium text-slate-500">{label}</span>
        <div className="flex -my-1.5 -mr-1.5">
          {assignment ? (
            <>
              <IconAction icon={Pencil} label={`Edit ${label.toLowerCase()} stakeholder`} onClick={() => openEdit(assignment)} />
              <IconAction icon={Trash2} label={`Remove ${label.toLowerCase()} stakeholder`} danger onClick={() => del(assignment)} />
            </>
          ) : (
            <IconAction icon={Plus} label={`Add ${label.toLowerCase()} stakeholder`} onClick={() => openAdd(role)} />
          )}
        </div>
      </div>
      {assignment ? (
        <>
          <p className="text-sm font-medium text-slate-800">{assignment.stakeholder_name}</p>
          <p className="text-[12.5px] text-slate-600 tabular-nums">{assignment.profit_percentage}% of client profit</p>
          {assignment.notes && <p className="text-[12px] text-slate-500 mt-0.5 truncate">{assignment.notes}</p>}
        </>
      ) : <p className="text-[13px] text-slate-500">Not assigned</p>}
    </div>
  );

  return (
    <SectionCard title="Referral stakeholders" icon={UserCheck} bodyClass="p-4 space-y-2">
      <RoleRow label="Primary" assignment={primary} role="primary" />
      <RoleRow label="Secondary" assignment={secondary} role="secondary" />
      <Dialog open={!!modal} onClose={() => setModal(null)} size="sm"
        title={modal ? `${modal.role === 'primary' ? 'Primary' : 'Secondary'} stakeholder` : ''}
        footer={<>
          <button type="button" onClick={() => setModal(null)} className="btn-secondary">Cancel</button>
          <button type="submit" form="stakeholder-form" disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save'}</button>
        </>}>
        <form id="stakeholder-form" onSubmit={save} className="space-y-4">
          <Field label="Stakeholder" required>
            {(fid) => (
              <select id={fid} className="input-field" value={form.stakeholder_id} onChange={(e) => setForm((f) => ({ ...f, stakeholder_id: e.target.value }))}>
                <option value="">Choose a stakeholder…</option>
                {allStakeholders.filter((s) => s.is_active !== false).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </Field>
          <Field label="Share of client profit (%)" required hint="Percentage of this client's shift profit paid to the stakeholder.">
            {(fid) => (
              <input id={fid} type="number" step="0.01" min="0" max="100" inputMode="decimal" className="input-field tabular-nums"
                value={form.profit_percentage} onChange={(e) => setForm((f) => ({ ...f, profit_percentage: e.target.value }))} placeholder="For example: 5" />
            )}
          </Field>
          <Field label="Notes">
            {(fid) => <input id={fid} className="input-field" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Agreement details, referral date…" />}
          </Field>
        </form>
      </Dialog>
    </SectionCard>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ClientDetail() {
  const { id } = useParams();
  // On its own (flat route) the Overview clears the sidebar's client when it closes, as before.
  const record = useClientRecord(id, { withRisk: true, clearOnUnmount: true });
  const { client, reload } = record;

  const [allSupportNeeds, setAllSupportNeeds] = useState([]);
  const [priceBooks, setPriceBooks] = useState([]);
  const [goals, setGoals] = useState(null);
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    if (!id || id === 'undefined') return;
    api.get('/smart-match/support-needs').then((r) => setAllSupportNeeds(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/price-books').then((r) => setPriceBooks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get(`/clients/${id}/goals`).then((r) => setGoals(Array.isArray(r.data) ? r.data : [])).catch(() => setGoals([]));
    api.get(`/care-plans?client_id=${id}`).then((r) => setPlans(Array.isArray(r.data) ? r.data : [])).catch(() => setPlans([]));
  }, [id]);

  // Every edit sends the full record with only the edited fields changed (see clientPayload).
  async function saveClient(fields, done = 'Saved') {
    try {
      await api.put(`/clients/${id}`, { ...clientPayload(client), ...fields });
      toast.success(done);
      await reload();
      return true;
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); return false; }
  }

  async function saveSection(section, data) {
    try {
      await api.patch(`/clients/${id}/section`, { section, data });
      toast.success('Saved');
      reload();
      return true;
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save this section'); return false; }
  }

  if (!client) {
    return (
      <ClientFrame record={record}>
        {!record.error && (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_18rem] gap-6 items-start">
            <div className="space-y-4"><CardSkeleton lines={2} /><CardSkeleton lines={4} /><CardSkeleton /></div>
            <div className="space-y-4"><CardSkeleton /><CardSkeleton lines={1} /></div>
          </div>
        )}
      </ClientFrame>
    );
  }

  return (
    <ClientFrame record={record}>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_18rem] gap-6 items-start">
        {/* ── Main column ── */}
        <div className="min-w-0 space-y-4">
          <DetailsCard client={client} onSave={(fields) => saveClient(fields, 'Client updated')} />

          <ClientFundingPlans clientId={client.id} client={client} />

          <SectionCard title="Client information" icon={BookOpen} bodyClass="px-5 py-4 space-y-5">
            <GeneralInfoEditor value={client.general_information || ''}
              onSave={(val) => saveClient({ generalInformation: val })} />
            <div className="border-t border-slate-100 pt-4">
              <NoteList title="Need to know" noun="need-to-know note" items={client.need_to_know || []}
                onSave={(d) => saveSection('need_to_know', d)}
                emptyText="No need-to-know notes yet. Add what a new worker must read before their first shift." />
            </div>
            <div className="border-t border-slate-100 pt-4">
              <NoteList title="Useful information" noun="useful note" items={client.useful_information || []}
                onSave={(d) => saveSection('useful_information', d)}
                emptyText="No useful information yet. Add routines, preferences and tips that help a shift go well." />
            </div>
            <div className="border-t border-slate-100 pt-4">
              <SupportNeeds clientId={client.id} current={client.supportNeeds || []} all={allSupportNeeds} onSaved={reload} />
            </div>
          </SectionCard>

          <ClinicalCard client={client} saveSection={saveSection} />

          <ClientWorkerPreferences clientId={client.id} />

          <ClientServiceTemplates clientId={client.id} />
        </div>

        {/* ── Right column (stacks below on narrow screens) ── */}
        <div className="min-w-0 space-y-4">
          <ContactsCard client={client} onChanged={reload} />
          <TeamsCard teams={client.teams} />
          <PriceBookCard client={client} priceBooks={priceBooks} onChanged={reload} />
          <RecordsCard clientId={client.id} documents={client.documents} goals={goals} plans={plans} />
          <ClientStakeholders clientId={client.id} />
          {client.private_notes && (
            <SectionCard title="Private notes" icon={Lock} bodyClass="px-4 py-3">
              <p className="text-[12px] text-slate-500 mb-1">Admin only. Workers don't see these.</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{client.private_notes}</p>
            </SectionCard>
          )}
        </div>
      </div>
    </ClientFrame>
  );
}
